import NiceModal, { useModal } from '@ebay/nice-modal-react'
import type { ButtonHTMLAttributes, UIEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import classNames from '../../utils/classNames'
import closeModal from '../../utils/closeModal'
import { debounce } from '../../utils/common'
import Icon from '../icon'
import Modal from './modal'

const Blank = () => (
  <p className="flex-none h-[38px] w-full snap-start bg-yellow-500" />
)

const Button = ({
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={classNames(
      'w-[47%] text-base lg:text-xl text-center font-Avenir-Light px-0 py-[7px] rounded-[22px]',
      'transition-all duration-200 ease-in-out cursor-pointer disabled:opacity-60',
      className,
    )}
    {...props}>
    {children}
  </button>
)

export interface Item {
  label: string
  id: string | number
  disabled?: 1 | 0
  disabled_reason?: string
}

interface ScrollSelectorProps {
  items: Array<Item>
  selected: Item | null
  onOk: (item: Item) => void
}

const ScrollSelector = ({ items, onOk, selected }: ScrollSelectorProps) => {
  const modal = useModal()
  const { t } = useTranslation('common')
  const scrollRef = useRef<HTMLDivElement>(null)
  const [selectedTemp, setSelectedTemp] = useState<Item | null>(null)

  useEffect(() => {
    if (items.length === 0) return
    setSelectedTemp(prev => (prev === null ? items[0] : prev))
  }, [items])

  useEffect(() => {
    if (modal.visible) {
      const index = items.findIndex(item => item.id === selected?.id)
      if (index < 0) return
      scrollRef.current?.scrollTo({ top: index * 38, left: 0 })
    }
  }, [modal.visible])

  const onScroll = (e: UIEvent) => {
    const scrollTop = (e.target as HTMLDivElement).scrollTop
    setSelectedTemp(items[Math.round(scrollTop / 38)])
  }

  return (
    <Modal id="scroll-selector-modal" containerStyle="p-5" zIndex={207}>
      {/* Active border */}
      <div className="absolute z-[-1] top-[calc(50%-40px)] right-5 w-[calc(100%-40px)] h-[38px] border-y border-txt" />
      {/* Tooltip */}
      {selectedTemp?.disabled && selectedTemp?.disabled_reason && (
        <p
          className={classNames(
            'absolute z-[225] bottom-[210px] left-1/2 -translate-x-1/2 px-[15px] py-2.5 rounded-[5px] pointer-events-none',
            'flex items-center w-max max-w-[calc(100vw-40px)] transition-all duration-200 ease-in-out text-xs lg:text-sm text-[#202632] bg-[#eee] dark:bg-white',
          )}>
          <Icon
            id="triangle-left-2"
            width="10"
            height="14.5"
            className="absolute top-[calc(100%-5px)] right-1/2 translate-x-1/2 -rotate-90 w-[7px] fill-[#eee] dark:fill-white"
          />
          {selectedTemp.disabled_reason}
        </p>
      )}
      {/* ScrollList */}
      <div
        ref={scrollRef}
        onScroll={debounce(onScroll, 100)}
        className="relative flex flex-col h-[190px] overflow-x-hidden overflow-y-auto common-scrollbar [&::-webkit-scrollbar-thumb]:bg-[#2c223a] snap-mandatory snap-y">
        <Blank />
        <Blank />
        {items.map((item, index) => (
          <p
            className={classNames(
              'snap-start leading-[38px] place-content-center outline-[1px] outline-red-500 text-center font-Avenir-Light transition-all duration-200 ease-in-out cursor-pointer',
              item.id === selectedTemp?.id && !selectedTemp?.disabled
                ? 'text-txt'
                : 'text-[rgb(179,179,179)]',
            )}
            key={index}
            onClick={() =>
              scrollRef.current?.scrollTo({
                top: index * 38,
                left: 0,
                behavior: 'smooth',
              })
            }>
            {item.label}
          </p>
        ))}
        <Blank />
        <Blank />
      </div>
      <div className="flx-btw-ctr">
        <Button
          onClick={() => closeModal(modal)}
          className="border-[1.5px] border-[#888] dark:border-white dark:text-white">
          {t('cancel')}
        </Button>
        <Button
          disabled={!!selectedTemp?.disabled}
          onClick={() => {
            selectedTemp && onOk(selectedTemp)
            closeModal(modal)
          }}
          className="bg-ok-btn-bg">
          {t('ok')}
        </Button>
      </div>
    </Modal>
  )
}

export default NiceModal.create(ScrollSelector)
